import React from 'react';
import { Runbook } from '../types';
import { exportRunbookToMarkdown } from '../lib/export';

interface ReaderViewProps {
  runbook: Runbook;
  ownerEmail?: string;
  onBack: () => void;
}

export const ReaderView: React.FC<ReaderViewProps> = ({
  runbook,
  ownerEmail,
  onBack,
}) => {
  return (
    <div className="print-content max-w-3xl mx-auto space-y-8">
      {/* Header bar */}
      <div className="no-print flex items-center justify-between border-b border-[#232736] pb-4">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="text-xs font-mono text-[#8b92a5] hover:text-white transition"
          >
            &larr; CATALOG
          </button>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-mono">
            &#x25cf; UNLOCKED
          </span>
          <span className="text-xs font-mono text-[#8b92a5]">
            {ownerEmail ? `SIG: ${ownerEmail}` : 'REGISTERED OPERATOR'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => exportRunbookToMarkdown(runbook)}
            className="px-3 py-1.5 bg-[#181b26] border border-[#232736] hover:border-sky-500/40 text-xs font-mono text-white rounded transition flex items-center gap-1.5"
          >
            <svg className="w-3.5 h-3.5 text-sky-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Export .md
          </button>
          <button
            onClick={() => window.print()}
            className="px-3 py-1.5 bg-[#181b26] border border-[#232736] hover:border-sky-500/40 text-xs font-mono text-white rounded transition flex items-center gap-1.5"
          >
            <svg className="w-3.5 h-3.5 text-sky-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4H9v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            Print PDF
          </button>
        </div>
      </div>

      {/* Document Body */}
      <article className="space-y-8">
        <header className="space-y-2 border-b border-[#232736] pb-6">
          <p className="text-xs font-mono text-sky-400 uppercase tracking-widest">
            PROTOCOL // {runbook.code} · {runbook.category.toUpperCase()}
          </p>
          <h1 className="font-tech text-3xl sm:text-4xl font-bold text-white">
            {runbook.title}
          </h1>
          <p className="text-sm font-mono text-[#8b92a5] italic">
            {runbook.subtitle}
          </p>
          <div className="p-3.5 rounded bg-[#181b26] border border-[#232736] text-xs text-zinc-300 font-mono">
            DIRECTIVE: {runbook.promise}
          </div>
        </header>

        {/* Un-redacted Content Sections */}
        <div className="space-y-8 divide-y divide-[#232736]">
          {runbook.sections.map((s, idx) => (
            <div key={idx} className="pt-6 space-y-3">
              <h2 className="font-tech text-xl font-bold text-white">{s.heading}</h2>
              <div className="text-xs font-mono text-zinc-300 leading-relaxed whitespace-pre-line">
                {s.body}
              </div>
            </div>
          ))}
        </div>

        {/* Distribution Copy Kit */}
        <div className="no-print border-t border-[#232736] pt-8 space-y-6">
          <h3 className="font-tech text-xl font-bold text-white">
            Deployment & Distribution Kit
          </h3>
          <p className="text-xs text-[#8b92a5] font-mono">
            Use these pre-tested outreach templates and distribution posts to roll out this protocol.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="p-4 rounded-xl blueprint-card space-y-2">
              <h4 className="text-xs font-mono uppercase text-sky-400">
                Distribution Posts (X / LinkedIn)
              </h4>
              <ul className="space-y-2 text-xs text-zinc-300 font-mono">
                {runbook.posts.map((post, i) => (
                  <li key={i} className="p-2.5 rounded bg-[#181b26] border border-[#232736] leading-relaxed">
                    {post}
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-4 rounded-xl blueprint-card space-y-2">
              <h4 className="text-xs font-mono uppercase text-sky-400">
                Direct Notice & Email Cadence
              </h4>
              <div className="space-y-3 text-xs text-zinc-300 font-mono">
                {runbook.emails.map((em, i) => (
                  <div key={i} className="p-2.5 rounded bg-[#181b26] border border-[#232736] space-y-1">
                    <div className="font-bold text-sky-400">SUBJECT: {em.subject}</div>
                    <div className="text-[#8b92a5]">{em.body}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
};
